import { Fragment } from 'react';

import { useCartOfferValidation } from 'features/cart/hooks/use-cart-offer-validation';
import { commonStyles } from 'features/cart/styles';
import { ICartEntry, MinimalCartEntryType } from 'features/cart/types';
import { isIncentiveOrCombo } from 'features/cart/utils';
import { useIntl } from 'react-intl';
import { StyleSheet, Text, View } from 'react-native';
import { Icon } from 'ucl/components/icon/Icon';

import { Skeleton } from 'components/ui/skeleton';
import { WalmartBadgeContainer } from 'pages/cart/cart-item/cart-item-offer';
import { componentsLibraryPrimitives, primitive } from 'styles/constants/primitives';

import { CartEntryFabButton } from './cart-entry-action-buttons';

const IncentiveHeaderOfferFeedback = ({ cartEntry }: { cartEntry: ICartEntry }) => {
  const { feedbackMessages, isOfferApplied, isLoading } = useCartOfferValidation(cartEntry);
  const { formatMessage } = useIntl();

  const offerMessage = formatMessage({
    id: isOfferApplied ? 'offerApplied' : 'offerNotApplied',
  });
  const textSeparator = ' - ';

  if (isLoading) {
    return <Skeleton shape="rectangle" style={{ width: '80%', height: 12, marginVertical: 0 }} />;
  }

  return (
    <View style={{ gap: 2, flexDirection: 'row', alignItems: 'center' }}>
      <Icon marginRight={1} variant={isOfferApplied ? 'checkFill' : 'errorV2'} size="3" />
      <Text style={styles.feedbackText}>
        {offerMessage}
        {feedbackMessages?.map((message, index) => (
          <Fragment key={index}>
            {textSeparator}
            {message}
          </Fragment>
        ))}
      </Text>
    </View>
  );
};

const CartEntryIncentiveHeader = ({ cartEntry }: { cartEntry: ICartEntry }) => {
  const { isOfferApplied, isLoading, walmart } = useCartOfferValidation(cartEntry);

  let headerBackgroundColor: string;
  if (isLoading) {
    headerBackgroundColor = componentsLibraryPrimitives.$blackOpacity10;
  } else {
    headerBackgroundColor = isOfferApplied ? primitive.bk.$successLight : primitive.bk.$errorLight;
  }

  return (
    <View
      style={[
        styles.header,
        {
          gap: 2,
          flexDirection: 'column',
          alignItems: 'flex-start',
          backgroundColor: headerBackgroundColor,
        },
      ]}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
        {(walmart.isOfferSelected || walmart.isLoading) && <WalmartBadgeContainer />}
        <Text style={commonStyles.cartEntryItemName}>{cartEntry.name}</Text>
      </View>
      <IncentiveHeaderOfferFeedback cartEntry={cartEntry} />
    </View>
  );
};

const CartEntryComboHeader = ({ cartEntry }: { cartEntry: ICartEntry }) => {
  return (
    <>
      <CartEntryFabButton cartEntryId={cartEntry.cartId} />
      {isIncentiveOrCombo(cartEntry) && (
        <View style={styles.header}>
          <Text style={commonStyles.cartEntryItemName}>{cartEntry.name}</Text>
        </View>
      )}
    </>
  );
};

export const CartEntryHeader = ({ cartEntry }: { cartEntry: ICartEntry }) => {
  if (cartEntry.type === MinimalCartEntryType.combo) {
    return <CartEntryComboHeader cartEntry={cartEntry} />;
  }

  if (cartEntry.type === MinimalCartEntryType.incentive) {
    return <CartEntryIncentiveHeader cartEntry={cartEntry} />;
  }

  return null;
};

const styles = StyleSheet.create({
  header: {
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    flexDirection: 'row',
    gap: 10,
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 14,
    ...commonStyles.withBorderBottom,
  },
  feedbackText: {
    fontSize: 10,
    fontFamily: primitive.fontFamily.$body,
    color: primitive.bk.$bbqBrown,
  },
});
